import { Button, EmptyState } from '@dev-spendesk/grapes';
import { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { BetaFeatureNotice } from 'src/core/common/components/BetaFeatureNotice';
import { HeaderWithNavigation } from 'src/core/common/components/HeaderWithNavigation/HeaderWithNavigation';
import { ErrorBoundary } from 'src/core/common/components/withErrorBoundary';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import FEATURES from 'src/core/constants/features';
import { routeFor, routes } from 'src/core/constants/routes';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';
import { useUserRoles } from 'src/core/modules/app/hooks/useUserRoles';
import { useCostCentersQuery } from 'src/core/modules/budgets/apis';
import { useGetBudgetDefinitions } from 'src/core/modules/budgets/budgets-by-dimensions/hooks/useGetBudgetDefinitions';
import { type BudgetaryExercise } from 'src/core/modules/budgets/models/budgetaryExercise';

import { useBudgetaryExercisesQuery } from '../../../../containers/hooks/useBudgetaryExercisesQuery';
import { sortNamedItems } from '../../../../utils/sortNamedItems';
import { BudgetaryExercisesPageSkeleton } from '../../v1/NewBudgetaryExercisesPage/BudgetaryExercisesPageSkeleton';
import { BudgetCardV2 } from '../BudgetCardV2';
import { BudgetaryExerciseCardV2 } from '../BudgetaryExerciseCardV2';
import { usePeriodRanges } from '../hooks/usePeriodRanges';
/**
 * BudgetsV2Page - List page for Budgets V2 Definitions
 *
 * This component displays a flat list of budget definitions.
 */
export const BudgetsPageV2 = () => {
  const companyId = useCompanyId();
  const history = useHistory();
  const { t, activeLanguage } = useTranslation('global');
  const { isAccountOwner, isController } = useUserRoles();

  const budgetDefinitionsQuery = useGetBudgetDefinitions();
  const budgetDefinitions = budgetDefinitionsQuery.data ?? [];

  const budgetaryExercisesQuery = useBudgetaryExercisesQuery();
  const budgetaryExercisesRaw =
    budgetaryExercisesQuery.status === 'success'
      ? budgetaryExercisesQuery.data
      : [];

  const costCentersQuery = useCostCentersQuery({ withDeleted: true });
  const costCenters =
    costCentersQuery.status === 'success' ? costCentersQuery.data : [];

  // Enrich budgetary exercises with cost center names
  const budgetaryExercises = useMemo(() => {
    if (
      budgetaryExercisesQuery.status !== 'success' ||
      costCentersQuery.status !== 'success'
    ) {
      return [];
    }

    return budgetaryExercisesRaw.map(
      (budgetaryExercise: BudgetaryExercise) => ({
        ...budgetaryExercise,
        budgets: budgetaryExercise.budgets.map((budget) => {
          const costCenter = costCenters.find(
            (cc) => cc.id === budget.costCenterId,
          );
          return {
            ...budget,
            name: costCenter?.name ?? '',
            isUnavailable: costCenter?.isDeleted,
          };
        }),
      }),
    );
  }, [
    budgetaryExercisesQuery.status,
    budgetaryExercisesRaw,
    costCentersQuery.status,
    costCenters,
  ]);

  const selectedPeriodRanges = usePeriodRanges();
  const canEditBudgets = isController || isAccountOwner;

  const handleRowClick = (budgetId: string, budgetRowId: string) => {
    const budgetsPageRoute = routeFor(routes.BUDGET_V2_OVERVIEW.path, {
      id: budgetId,
      company: companyId,
    });
    history.push({
      pathname: budgetsPageRoute,
      state: {
        hasBackButton: true,
        ...selectedPeriodRanges.getLocationState(),
        budgetRowId,
      },
    });
  };

  const handleV1RowClick = (budgetId: string, expenseCategoryId?: string) => {
    const budgetsPageRoute = expenseCategoryId
      ? routeFor(routes.SUB_BUDGET_OVERVIEW.path, {
          id: budgetId,
          subBudgetId: expenseCategoryId,
          company: companyId,
        })
      : routeFor(routes.BUDGET_OVERVIEW.path, {
          id: budgetId,
          company: companyId,
        });
    history.push({
      pathname: budgetsPageRoute,
      state: {
        hasBackButton: true,
      },
    });
  };

  const v2Budgets = sortNamedItems(budgetDefinitions, activeLanguage).map(
    (definition) => (
      <BudgetCardV2
        key={definition.id}
        budgetDefinition={definition}
        selectedPeriodRange={selectedPeriodRanges.getById(definition.id)}
        onSelectedPeriodRangeChange={(period) =>
          selectedPeriodRanges.setById(definition.id, period)
        }
        onRowClick={(rowId) => handleRowClick(definition.id, rowId)}
      />
    ),
  );

  const v1Budgets = sortNamedItems(budgetaryExercises, activeLanguage).map(
    (exercise) => (
      <BudgetaryExerciseCardV2
        key={exercise.id}
        budgetaryExercise={exercise}
        canEditBudgets={canEditBudgets}
        activeLanguage={activeLanguage}
        onRowClick={handleV1RowClick}
      />
    ),
  );

  // Early return for loading state - must come after all hooks
  if (
    budgetDefinitionsQuery.isLoading ||
    budgetaryExercisesQuery.status === 'loading'
  ) {
    return <BudgetaryExercisesPageSkeleton />;
  }

  const hasAnyBudgets =
    budgetDefinitions.length > 0 || budgetaryExercises.length > 0;

  return (
    <ErrorBoundary context={{ scope: 'budgets-v2', team: 'control' }}>
      <div className="page__container flex flex-col gap-32 pb-40">
        <HeaderWithNavigation
          links={[]}
          rightContent={
            <div className="flex items-center gap-16">
              <BetaFeatureNotice
                featureName={FEATURES.BUDGETS_BY_DIMENSIONS}
                feedbackTags={['budgets-v2']}
                showOnlyFeedbackModal
              />
              <Button
                text={t('budget.redesigned.createBudgetByDimensions')}
                onClick={() => {
                  history.push(
                    routeFor(routes.SETUP_BUDGET_BY_DIMENSIONS.path, {
                      company: companyId,
                    }),
                  );
                }}
              />
            </div>
          }
        >
          {t('navigation.budgets')}
        </HeaderWithNavigation>
        {!hasAnyBudgets ? (
          <EmptyState
            title={t('budget.redesigned.emptyState.title')}
            subtitle={t('budget.redesigned.emptyState.text')}
            actions={
              <Button
                text={t('budget.redesigned.createBudgetByDimensions')}
                onClick={() => {
                  history.push(
                    routeFor(routes.SETUP_BUDGET_BY_DIMENSIONS.path, {
                      company: companyId,
                    }),
                  );
                }}
              />
            }
            iconName="circle-information"
          />
        ) : null}
        <div className="flex w-full flex-col gap-24 self-center p-40 pt-0">
          {/* Budget V2 cards */}
          {v2Budgets}

          {/* Budget V1 cards */}
          {v1Budgets}
        </div>
      </div>
    </ErrorBoundary>
  );
};
